import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import noop from 'lodash/noop'
import { useTheme } from '@mui/styles'
import {
  useCreateMaterialsTableRow,
  getPrintRowValue,
} from 'components/generic/printable/table/simpleMaterialsTableHelpers'
import { getTotals, isDifferentialOversteppedScaleView } from 'components/containers/inbound-tickets/utils'
import { makeCommaSeparatedString } from 'utils/data-manipulation/valueFormatter'

const SimpleMaterialsTable = ({
  type,
  materials,
  truckWeight,
  subTotalOverride,
  taxRate,
  showTotal,
  showMaterialDeductionNotes,
  onClickRow,
  isPrint,
  setDifferential,
  canShowDifferentialRow,
  chargeback,
  pricePrepend,
  effectiveDateIBT,
  errors,
  isTransfer,
  feesAndPromotions,
  onRowDeleteClick,
}) => {
  const theme = useTheme()
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals, thresholdAmount, thresholdUM, flagManuallyEnteredWeights } =
    settings?.purchaseSettings?.inboundTickets || {}
  const { truckTotal, loadTotal } = getTotals(materials, truckWeight, showWeightDecimals)
  const differential = truckTotal - loadTotal
  const showDifferentialRow = canShowDifferentialRow && differential > 0
  const isDifferentialExceeded = isDifferentialOversteppedScaleView(
    differential,
    loadTotal,
    thresholdAmount,
    thresholdUM,
  )
  const showTax = taxRate > 0
  const showTruckWeight = Object.keys(truckWeight).length > 0
  const { canFlagManualScaleReadOverrideFF, enableMaterialPaymentHolds, enablePullApartFeatures } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const canFlagManualScaleReadOverride = canFlagManualScaleReadOverrideFF && flagManuallyEnteredWeights
  const { customer: supplier } = useSelector(state => state.customer)
  const createMaterialsTableRow = useMemo(
    () =>
      // eslint-disable-next-line
      useCreateMaterialsTableRow({
        type,
        materials,
        truckWeight,
        subTotalOverride,
        taxRate,
        isPrint,
        showWeightDecimals,
        thresholdAmount,
        thresholdUM,
        canFlagManualScaleReadOverride,
        chargeback,
        pricePrepend,
        enableMaterialPaymentHolds,
        supplier,
        effectiveDateIBT,
        enablePullApartFeatures,
        isTransfer,
        onRowDeleteClick,
        defaultYardUm,
      }),
    [
      type,
      materials,
      truckWeight,
      subTotalOverride,
      taxRate,
      isPrint,
      showWeightDecimals,
      thresholdAmount,
      thresholdUM,
      canFlagManualScaleReadOverride,
      chargeback,
      pricePrepend,
      enableMaterialPaymentHolds,
      supplier,
      effectiveDateIBT,
      enablePullApartFeatures,
      isTransfer,
    ],
  )

  const columnCount = createMaterialsTableRow().filter(column => column.qualifier).length

  useEffect(() => {
    setDifferential(differential)
  }, [materials, truckWeight])

  return (
    <TableContainer data-testid="materials-table">
      <Table
        size="small"
        aria-label="a simple materials table"
        sx={{
          tableLayout: isPrint ? 'fixed' : 'auto',
          td: {
            fontSize: isPrint ? '13px' : '14px',
            padding: isPrint ? '4px 1px' : '8px 4px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            verticalAlign: 'top',
            height: 'auto !important',
            lineHeight: isPrint ? '0.8' : '1',
          },
          'td .MuiTypography-root': {
            fontSize: isPrint ? '13px' : '14px',
            lineHeight: isPrint ? '0.8' : '1',
          },
          th: {
            fontSize: isPrint ? '12px' : '16px',
            padding: isPrint ? '4px 1px' : '8px 4px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
        }}
      >
        <TableHead sx={{ th: { fontWeight: '600' } }}>
          <TableRow>
            {createMaterialsTableRow().map(column =>
              column.qualifier ? <TableCell {...column.cellProps}>{column.headerRowValue}</TableCell> : null,
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {materials.map(material => (
            <React.Fragment key={material.id}>
              {isPrint && (
                <TableRow>
                  <TableCell
                    scope="row"
                    key={`${material?.id}-print-row`}
                    colSpan={columnCount}
                    sx={{ borderBottom: 'none', fontWeight: '600' }}
                  >
                    {getPrintRowValue({ rowData: material, type, pricePrepend, defaultYardUm })}
                  </TableCell>
                </TableRow>
              )}
              <TableRow
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onClickRow(material.id)}
                data-testid="material-row"
              >
                {createMaterialsTableRow(material, errors).map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.materialRowValue}</TableCell> : null,
                )}
              </TableRow>

              {showMaterialDeductionNotes &&
                material?.weightAndPrice?.deductions?.length > 0 &&
                material?.weightAndPrice?.deductions
                  ?.filter(deduction => !!deduction.notes)
                  ?.map(deduction => (
                    <TableRow key={deduction.id}>
                      <TableCell colSpan={columnCount - 1}>
                        <Box pl={1}>
                          <Typography variant="caption">
                            {deduction.notes && (
                              <>
                                <span>Notes</span>: {deduction.notes} ({deduction.typeText} {deduction.unitText})
                              </>
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
            </React.Fragment>
          ))}
          {type === 'PRINT_PURCHASE_TICKET' && chargeback > 0 && (
            <>
              {isPrint && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      borderBottom: 'none',
                      fontWeight: '600',
                    }}
                  >
                    Freight Charge
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ td: { fontSize: '13px' } }}>
                {createMaterialsTableRow().map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.chargebackRowValue}</TableCell> : null,
                )}
              </TableRow>
            </>
          )}
          {type === 'PRINT_PURCHASE_TICKET' &&
            feesAndPromotions.length > 0 &&
            feesAndPromotions.map(feeOrPromotion => (
              <React.Fragment key={feeOrPromotion.id}>
                {isPrint && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      sx={{
                        borderBottom: 'none',
                        fontWeight: '600',
                      }}
                    >
                      <Stack direction="row" alignItems="center">
                        {feeOrPromotion?.name}
                        {feeOrPromotion?.showPaymentMethodSpecificIcon && (
                          <Tooltip
                            title={`Only applies to ${makeCommaSeparatedString(feeOrPromotion.eligiblePaymentTypes)} payments`}
                          >
                            <InfoIcon sx={{ fontSize: '14px', color: 'text.secondary', ml: 1 }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  {createMaterialsTableRow(feeOrPromotion).map(column =>
                    column.qualifier ? (
                      <TableCell {...column.cellProps}>{column.feeOrPromotionRowValue}</TableCell>
                    ) : null,
                  )}
                </TableRow>
              </React.Fragment>
            ))}
          {showTax && (
            <>
              {isPrint && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      borderBottom: 'none',
                      fontWeight: '600',
                    }}
                  >
                    Subtotal
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {createMaterialsTableRow().map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.subTotalRowValue}</TableCell> : null,
                )}
              </TableRow>
              {isPrint && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      borderBottom: 'none',
                      fontWeight: '600',
                    }}
                  >
                    {`Tax (${taxRate}%)`}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                {createMaterialsTableRow().map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.taxRowValue}</TableCell> : null,
                )}
              </TableRow>
            </>
          )}
          {showTotal && (
            <>
              {isPrint && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      borderBottom: 'none',
                      fontWeight: '600',
                    }}
                  >
                    {taxRate > 0 ? 'Total After Tax' : 'Total'}
                  </TableCell>
                </TableRow>
              )}
              <TableRow
                sx={{
                  'td, th': {
                    fontWeight: 'bold',
                    backgroundColor: isPrint
                      ? 'transparent'
                      : type === 'PURCHASE_ORDER' || type === 'SALES_ORDER'
                        ? 'lightgray'
                        : theme.palette.info.lightBg,
                  },
                }}
              >
                {createMaterialsTableRow().map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.totalRowValue}</TableCell> : null,
                )}
              </TableRow>
            </>
          )}
          {showTruckWeight && (
            <>
              {isPrint && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    sx={{
                      borderBottom: 'none',
                      fontWeight: '600',
                    }}
                  >
                    Truck Total
                  </TableCell>
                </TableRow>
              )}
              <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onClickRow('truck')} data-testid="truck-total">
                {createMaterialsTableRow().map(column =>
                  column.qualifier ? <TableCell {...column.cellProps}>{column.truckRowValue}</TableCell> : null,
                )}
              </TableRow>
            </>
          )}
          {showDifferentialRow && (
            <TableRow
              sx={{
                cursor: 'pointer',
                background: isDifferentialExceeded ? theme.palette.error.background : 'unset',
                '&:hover': {
                  background: isDifferentialExceeded ? theme.palette.error.hover : 'unset',
                },
              }}
              onClick={() => onClickRow('differential')}
              data-testid="differential"
            >
              {createMaterialsTableRow().map(column =>
                column.qualifier ? <TableCell {...column.cellProps}>{column.differentialRowValue}</TableCell> : null,
              )}
            </TableRow>
          )}
          {type === 'SCALE_VIEW_TICKET' && chargeback > 0 && (
            <TableRow>
              {createMaterialsTableRow().map(column =>
                column.qualifier ? <TableCell {...column.cellProps}>{column.chargebackRowValue}</TableCell> : null,
              )}
            </TableRow>
          )}
          {type === 'SCALE_VIEW_TICKET' &&
            feesAndPromotions.length > 0 &&
            feesAndPromotions.map(feeOrPromotion => (
              <React.Fragment key={feeOrPromotion.id}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onClickRow(feeOrPromotion.id)}
                  data-testid="fee-or-promotion-row"
                >
                  {createMaterialsTableRow(feeOrPromotion).map(column =>
                    column.qualifier ? (
                      <TableCell {...column.cellProps}>{column.feeOrPromotionRowValue}</TableCell>
                    ) : null,
                  )}
                </TableRow>
              </React.Fragment>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// todo have a proptype for inbound ticket materials
SimpleMaterialsTable.propTypes = {
  type: PropTypes.string.isRequired,
  materials: PropTypes.arrayOf(PropTypes.object),
  truckWeight: PropTypes.object,
  subTotalOverride: PropTypes.number,
  taxRate: PropTypes.number,
  showTotal: PropTypes.bool,
  showMaterialDeductionNotes: PropTypes.bool,
  onClickRow: PropTypes.func,
  isPrint: PropTypes.bool,
  setDifferential: PropTypes.func,
  canShowDifferentialRow: PropTypes.bool,
  chargeback: PropTypes.number,
  pricePrepend: PropTypes.string,
  effectiveDateIBT: PropTypes.object,
  errors: PropTypes.object,
  isTransfer: PropTypes.bool,
  feesAndPromotions: PropTypes.array,
  onRowDeleteClick: PropTypes.func,
}

SimpleMaterialsTable.defaultProps = {
  materials: [],
  truckWeight: {},
  subTotalOverride: null,
  taxRate: 0,
  showTotal: true,
  showMaterialDeductionNotes: false,
  onClickRow: noop,
  isPrint: false,
  setDifferential: noop,
  canShowDifferentialRow: false,
  chargeback: 0,
  pricePrepend: '$',
  effectiveDateIBT: {},
  errors: {},
  isTransfer: false,
  feesAndPromotions: [],
  onRowDeleteClick: noop,
}

export default SimpleMaterialsTable
